import BaseComponent from '@/components/base';
import { style } from './style';
import { FontStyle, ButtonStyles } from '@/components/style';

export default class CustomSelect extends BaseComponent {
  // Getter methods for shadow root elements
  get button() {
    return this.shadowRoot.querySelector('.select-button');
  }

  get dropdown() {
    return this.shadowRoot.querySelector('.select-dropdown');
  }

  get options() {
    return Array.from(this.shadowRoot.querySelectorAll('.select-option'));
  }

  get hiddenSelect() {
    return this.shadowRoot.querySelector('[data-original-select]');
  }

  get buttonText() {
    return this.shadowRoot.querySelector('.select-button-text');
  }

  get selectLabel() {
    return this.shadowRoot.querySelector('.select-label');
  }

  get selectWrapper() {
    return this.shadowRoot.querySelector('.select-wrapper');
  }

  get customSelect() {
    return this.shadowRoot.querySelector('[data-custom-select]');
  }

  render() {
    const label = this.getAttribute('label');
    const name = this.getAttribute('name') || 'custom-select';

    // Get options from child elements or data attribute
    const options = this.getOptions();

    return `
      ${style}
      ${FontStyle}
      ${ButtonStyles}
      <div class="custom-select" data-custom-select>
        ${
          label
            ? `<label class="select-label" for="select-${name}">${label}</label>`
            : ''
        }
        <div class="select-wrapper">
          <button 
            class="select-button" 
            id="select-${name}"
            role="combobox"
            aria-haspopup="listbox"
            aria-expanded="false"
            aria-controls="dropdown-${name}"
            aria-labelledby="label-${name}"
            type="button"
          >
            <span class="select-button-text">${options[0].label}</span>
          </button>
          
          <ul 
            class="select-dropdown" 
            id="dropdown-${name}" 
            role="listbox" 
            tabindex="-1"
            aria-expanded="false"
          >
            ${options
              .map(
                (option, index) => `
              <li 
                class="select-option"
                id="option-${name}-${index}"
                role="option"
                data-value="${option.value}"
                aria-selected=${index === 0 ? 'true' : 'false'}
                tabindex="-1"
              >
                ${option.label}
              </li>
            `
              )
              .join('')}
          </ul>
          
          <select 
            name="${name}" 
            class="hidden-select" 
            aria-hidden="true"
            data-original-select
          >
            ${options
              .map(
                (option) => `
              <option option="${name}" value="${option.value}">${option.label}</option>
            `
              )
              .join('')}
          </select>
        </div>
      </div>
    `;
  }

  getOptions() {
    // Try to get options from child elements first
    const optionElements = this.querySelectorAll('option');
    if (optionElements.length > 0) {
      return Array.from(optionElements).map((opt) => ({
        value: opt.value || opt.textContent,
        label: opt.textContent
      }));
    }

    return [];
  }

  afterMount() {
    this._cacheDom();
    this._setupEventListeners();
    this._initValue();
  }

  resetToDefault() {
    this._selectOption(0);
  }

  _cacheDom() {
    // Cache DOM elements and state variables
    this.currentIndex = -1;
    this.isOpen = false;
  }

  _setupEventListeners() {
    // Create utility functions
    this._createUtilityFunctions();

    // Set up all event listeners
    this._setupButtonListeners();
    this._setupOptionListeners();
    this._setupKeyboardListeners();
    this._setupOutsideClickListeners();
    this._setupFocusListeners();
  }

  _createUtilityFunctions() {
    this._openDropdown = () => {
      this.dropdown.setAttribute('aria-expanded', 'true');
      this.button.setAttribute('aria-expanded', 'true');
      this.dropdown.style.display = 'block';
      this.dropdown.focus();
      this.isOpen = true;
    };

    this._closeDropdown = () => {
      this.dropdown.setAttribute('aria-expanded', 'false');
      this.button.setAttribute('aria-expanded', 'false');
      this.dropdown.style.display = 'none';
      this.isOpen = false;
    };

    this._selectOption = (index) => {
      if (index < 0 || index >= this.options.length) {
        return;
      }

      // Update visual state
      this.options.forEach((opt, i) => {
        opt.setAttribute('aria-selected', i === index ? 'true' : 'false');
      });

      const selectedOption = this.options[index];
      this.buttonText.textContent = selectedOption.textContent;
      this.hiddenSelect.value = selectedOption.dataset.value;
      this.currentIndex = index;

      // Dispatch change event
      const changeEvent = new Event('change', { bubbles: true });
      this.hiddenSelect.dispatchEvent(changeEvent);

      // Dispatch custom event
      const customEvent = new CustomEvent('custom-select-change', {
        bubbles: true,
        detail: {
          value: selectedOption.dataset.value,
          label: selectedOption.textContent,
          index: index
        }
      });
      this.dispatchEvent(customEvent);

      this._closeDropdown();
      this.button.focus();
    };
  }

  _setupButtonListeners() {
    // Button click handler
    this.button.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      if (this.isOpen) {
        this._closeDropdown();
      } else {
        this._openDropdown();
      }
    });
  }

  _setupOptionListeners() {
    // Option click handlers
    this.options.forEach((option, index) => {
      option.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this._selectOption(index);
      });

      option.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          this._selectOption(index);
        }
      });
    });
  }

  _setupKeyboardListeners() {
    // Keyboard navigation for button
    this.button.addEventListener('keydown', (e) => {
      if (!['ArrowDown', 'ArrowUp', 'Enter', ' '].includes(e.key)) {
        return;
      }

      e.preventDefault();

      if (this.isOpen) {
        return;
      }

      this._openDropdown();

      if (e.key === 'ArrowDown') {
        this.currentIndex = 0;
        this.options[this.currentIndex].focus();
      } else if (e.key === 'ArrowUp') {
        this.currentIndex = this.options.length - 1;
        this.options[this.currentIndex].focus();
      }
    });

    // Keyboard navigation for dropdown
    this.dropdown.addEventListener('keydown', (e) => {
      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          this.currentIndex = (this.currentIndex + 1) % this.options.length;
          this.options[this.currentIndex].focus();
          break;

        case 'ArrowUp':
          e.preventDefault();
          this.currentIndex =
            (this.currentIndex - 1 + this.options.length) % this.options.length;
          this.options[this.currentIndex].focus();
          break;

        case 'Enter':
        case ' ':
          e.preventDefault();
          this._selectOption(this.currentIndex);
          break;

        case 'Escape':
          this._closeDropdown();
          this.button.focus();
          break;

        case 'Home':
          e.preventDefault();
          this.currentIndex = 0;
          this.options[this.currentIndex].focus();
          break;

        case 'End':
          e.preventDefault();
          this.currentIndex = this.options.length - 1;
          this.options[this.currentIndex].focus();
          break;
      }
    });
  }

  _setupOutsideClickListeners() {
    // Close dropdown when clicking outside
    document.addEventListener('click', (e) => {
      const target = e.target;
      let isInComponent = false;

      if (target === this) {
        isInComponent = true;
      } else if (this.shadowRoot) {
        try {
          isInComponent = this.shadowRoot.contains(target);
        } catch (error) {
          // Silent error handling
        }
      } else if (this.contains(target)) {
        isInComponent = true;
      }

      const isOutside = !isInComponent;

      if (isOutside && this.isOpen) {
        this._closeDropdown();
      }
    });

    // Prevent clicks inside dropdown from closing it
    this.dropdown.addEventListener('click', (e) => {
      e.stopPropagation();

      if (e.target.classList.contains('select-option')) {
        const index = this.options.indexOf(e.target);
        if (index !== -1) {
          this._selectOption(index);
        }
      }
    });

    // Add mousedown listener to shadowRoot for better event capture
    this.shadowRoot.addEventListener('mousedown', (e) => {
      if (e.target.classList.contains('select-option')) {
        e.preventDefault();
        e.stopPropagation();
        const index = this.options.indexOf(e.target);
        if (index !== -1) {
          this._selectOption(index);
        }
      }
    });
  }

  _setupFocusListeners() {
    // Handle focus management
    this.dropdown.addEventListener('focusout', (e) => {
      setTimeout(() => {
        if (!this.shadowRoot.contains(document.activeElement)) {
          this._closeDropdown();
        }
      }, 0);
    });
  }

  _initValue() {
    // Set initial value if provided
    const initialValue = this.getAttribute('value');
    if (initialValue) {
      const initialIndex = this.options.findIndex(
        (opt) => opt.dataset.value === initialValue
      );
      if (initialIndex !== -1) {
        this._selectOption(initialIndex);
      }
    }
  }

  // Public method to set value programmatically
  setValue(value) {
    const index = this.options.findIndex((opt) => opt.dataset.value === value);
    if (index !== -1 && this._selectOption) {
      this._selectOption(index);
    }
  }

  // Public method to get current value
  getValue() {
    return this.hiddenSelect.value;
  }

  // Public method to get current label
  getLabel() {
    return this.buttonText.textContent;
  }
}
